import { personFirstName } from '../personName.js';

// THE TEXT CONVERSATION AS A MESSAGE THREAD (Leads centre polish, A5). Pure: consecutive messages from the same side and sender are ONE
// group with ONE time under it, and a system line ("Matt took over the texts. Sam is paused.") sits between groups, centred.
//
// ⚠️ "Sam" is the club's assistant, whatever it is called there; a PERSON'S message (staff or the club's own team) is a third side with the
// sender's FIRST NAME above it. An honorific is never a name ("Mr" -> "Your team"), and a person's message with no name at all reads
// "Your team". The colour is never the only signal: every group carries its sender (visible for a person, read out for the others).

export interface ThreadMessage {
  id: string;
  at: string;
  from: 'lead' | 'assistant' | 'note';
  text: string;
  byStaff?: boolean;
  byName?: string;
}

export type ThreadSide = 'lead' | 'assistant' | 'person';

export type ThreadItem =
  | { kind: 'note'; id: string; text: string }
  | { kind: 'group'; key: string; side: ThreadSide; sender: string | null; at: string; messages: Array<{ id: string; text: string }> };

/** Which side a real message is on. A message the assistant did not write (a person's) is `person`. */
export function sideOf(m: Pick<ThreadMessage, 'from' | 'byStaff' | 'byName'>): ThreadSide {
  if (m.from === 'lead') return 'lead';
  return m.byStaff || m.byName ? 'person' : 'assistant';
}

/** The name above a person's message: their first name ("Kyck" stays as the server sent it), else "Your team". */
export function personSender(m: Pick<ThreadMessage, 'byName'>): string {
  return personFirstName(m.byName) ?? 'Your team';
}

/** Oldest first, as a thread reads. The caller passes the messages it wants shown (the last few, or all). */
export function groupThread(messages: readonly ThreadMessage[]): ThreadItem[] {
  const out: ThreadItem[] = [];
  for (const m of messages) {
    if (m.from === 'note') {
      out.push({ kind: 'note', id: m.id, text: m.text });
      continue;
    }
    const side = sideOf(m);
    const sender = side === 'person' ? personSender(m) : null;
    const last = out[out.length - 1];
    if (last && last.kind === 'group' && last.side === side && last.sender === sender) {
      last.messages.push({ id: m.id, text: m.text });
      last.at = m.at;
    } else {
      out.push({ kind: 'group', key: m.id, side, sender, at: m.at, messages: [{ id: m.id, text: m.text }] });
    }
  }
  return out;
}

/** "3:30 pm" in the club's zone (the viewer's own when none is known). An unreadable time reads "". */
export function threadTime(iso: string, timeZone?: string | null): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
  } catch {
    return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  }
}
